import { Controller } from "@hotwired/stimulus";

// Pins a wide table's header row to the top of the window while its rows scroll by. `position: sticky` can't:
// the frame scrolls sideways, which makes it the sticky container, and it never scrolls down.
// The header cells slide down instead, by an animation tied to the page scroll (a ViewTimeline on the frame), so
// the browser moves them on its scroll thread in the same frame as the rows, as it does a sticky header. A script
// moving them on `scroll` lands a frame late and twitches (owner 2026-09-30). This controller only measures, when
// the table's size changes. Browsers without scroll timelines (Firefox for now) get the script path.
export default class extends Controller {
  connect() {
    this.animations = [];
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };

    // The frame, not the table: the table's nearest scroller is the frame, which never scrolls down.
    this.timeline = "ViewTimeline" in window ? new ViewTimeline({ subject: this.element, axis: "block" }) : null;
    if (!this.timeline) {
      window.addEventListener("scroll", this.schedule, { passive: true });
      window.addEventListener("resize", this.schedule);
    }
    // Paging and sorting change the table's height without scrolling the window.
    this.resizeObserver = new ResizeObserver(this.schedule);
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    window.removeEventListener("scroll", this.schedule);
    window.removeEventListener("resize", this.schedule);
    this.resizeObserver.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.cancelAnimations();
  }

  update() {
    const table = this.element.querySelector("table");
    const head = table && table.tHead;
    if (!head) return;

    const room = Math.max(0, table.offsetHeight - head.offsetHeight);
    if (this.timeline) {
      this.animate(head, table, room);
    } else {
      const passed = -table.getBoundingClientRect().top;
      const shift = Math.max(0, Math.min(passed, room));
      head.style.setProperty("--sticky-table-head-shift", `${shift}px`);
    }
  }

  // One pixel of slide per pixel of page scroll, from the table's top reaching the window's top until its last row
  // does; `fill: both` holds the ends.
  animate(head, table, room) {
    const start = table.getBoundingClientRect().top - this.element.getBoundingClientRect().top;
    this.cancelAnimations();
    this.animations = [...head.rows[0].cells].map((cell) =>
      cell.animate([{ transform: "translateY(0)" }, { transform: `translateY(${room}px)` }], {
        timeline: this.timeline,
        fill: "both",
        rangeStart: `exit-crossing ${start}px`,
        rangeEnd: `exit-crossing ${start + room}px`,
      })
    );
  }

  cancelAnimations() {
    this.animations.forEach((animation) => animation.cancel());
    this.animations = [];
  }
}
